// =============================================================================
// SIREPPAR - Página de Gestión de Autoridades y Órganos
// =============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { partidoService, padronService, documentoService, usuarioService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TIPO_ORGANO_CERTIFICADORES,
  CARGO_CERTIFICADOR_FIRMAS,
  LABEL_CERTIFICADORES,
} from '../../utils/certificadoresFirmas';

interface Mandato {
  id: string;
  cargo: string | null;
  esPromotor?: boolean;
  esPresidente: boolean;
  esTitular: boolean;
  ordenSuplencia?: number;
  mandatoDesde: string;
  mandatoHasta?: string;
  activo: boolean;
}

interface Autoridad {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  genero: string;
  fechaNacimiento?: string;
  email?: string;
  telefono?: string;
  organo?: { id: string; denominacion: string; denominacionPersonalizada?: string; fechaMandatoDesde?: string; fechaMandatoHasta?: string };
  mandatos?: Mandato[];
}

interface Apoderado {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  genero?: string;
  tipo: string;
  esIniciador: boolean;
  esPromotor?: boolean;
  email?: string;
  telefono?: string;
  activo: boolean;
  fechaDesignacion: string;
  fechaCese?: string;
}

interface Organo {
  id: string;
  tipoOrgano: string;
  denominacion: string;
  denominacionPersonalizada?: string;
  esObligatorio: boolean;
  fechaMandatoDesde?: string;
  fechaMandatoHasta?: string;
}

interface UsuarioPartido {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  rol: string;
  estado: string;
}

const generoLabels: Record<string, string> = {
  MASCULINO: 'Masculino',
  FEMENINO: 'Femenino',
  NO_BINARIO: 'No Binario',
  OTRO: 'Otro',
};

const CARGOS_AUTORIDAD = [
  'PRESIDENTE',
  'VICEPRESIDENTE',
  'SECRETARIO GENERAL',
  'SECRETARIO DE ACTAS',
  'TESORERO',
  'PROTESORERO',
  'VOCAL TITULAR',
  'VOCAL SUPLENTE',
  'REVISOR DE CUENTAS',
  // Cargo de la sección de Certificadores de Firmas: quienes avalan las fichas de
  // afiliación y las planillas de adherentes. Se incluye acá para poder editar desde
  // la vista interna lo que cargaron los wizards (ver utils/certificadoresFirmas.ts).
  CARGO_CERTIFICADOR_FIRMAS,
  'OTRO',
];

// Órganos obligatorios (deben existir en todo partido). Solo se ofrecen al crear si faltan.
// CERTIFICADORES no es un órgano de conducción, pero se modela como tal para reusar la
// carga de personas; el partido lo ve como una "sección" (utils/certificadoresFirmas.ts).
const TIPOS_ORGANO_OBLIGATORIO: { value: string; label: string }[] = [
  { value: 'CONGRESO_CONVENCION', label: 'Congreso o Convención' },
  { value: 'COMITE_JUNTA_CONSEJO', label: 'Comité, Junta o Consejo' },
  { value: 'JUNTA_ELECTORAL', label: 'Junta Electoral' },
  { value: TIPO_ORGANO_CERTIFICADORES, label: LABEL_CERTIFICADORES },
];

// Órganos opcionales (siempre disponibles para crear)
const TIPOS_ORGANO_OPCIONAL: { value: string; label: string }[] = [
  { value: 'MESA_DIRECTIVA', label: 'Mesa Directiva' },
  { value: 'TRIBUNAL_CONDUCTA', label: 'Tribunal de Conducta' },
  { value: 'JUNTA_DEPARTAMENTAL', label: 'Junta Departamental' },
  { value: 'OTRO', label: 'Otro (especificar)' },
];

const organoLabel =(o: Organo | { denominacion: string; denominacionPersonalizada?: string }) =>
  o.denominacionPersonalizada || o.denominacion;

export default function AutoridadesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const esInterno = !user?.partidoId;
  const esAdminOSupervisor = ['ADMINISTRADOR', 'SUPERVISOR'].includes(user?.rol || '');
  // MESA_ENTRADAS es interno de SOLO LECTURA: ve las autoridades/apoderados pero no
  // puede gestionarlos (agregar, editar, bajar). Coincide con el backend, que excluye
  // a MESA_ENTRADAS de estos endpoints (usa Roles.INTERNOS_ESCRITURA).
  const esMesaEntradas = user?.rol === 'MESA_ENTRADAS';
  const puedeGestionarAutoridades = !esMesaEntradas;
  // Agregar autoridades/apoderados desactivado por ahora: falta la lógica de
  // marcarlos como promotores según el estado del expediente (ver pendiente).
  // Reactivar poniéndolo en true cuando esté definido el modelo de estados.
  const puedeAgregarMiembros = false;
  const [selectedPartidoId, setSelectedPartidoId] = useState('');

  const [verHistorico, setVerHistorico] = useState(false);
  const [organoExpandido, setOrganoExpandido] = useState<Record<string, boolean>>({});
  const [histExpandido, setHistExpandido] = useState<Record<string, boolean>>({});

  const partidoId = paramId || user?.partidoId || selectedPartidoId || undefined;

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------
  const { data: queryData, isLoading } = useQuery({
    queryKey: ['autoridades', partidoId],
    queryFn: async () => {
      if (!partidoId) return { autoridades: [] as Autoridad[], apoderados: [] as Apoderado[] };
      const response = await partidoService.getAutoridades(partidoId);
      return response.data.data as { autoridades: Autoridad[]; apoderados: Apoderado[] };
    },
    enabled: !!partidoId,
  });

  const todasAutoridades = queryData?.autoridades ?? [];
  const todosApoderados = queryData?.apoderados ?? [];

  const autoridadesVigentes = todasAutoridades.filter(a => a.mandatos?.[0]?.activo === true);
  const autoridadesHistoricas = todasAutoridades.filter(a => a.mandatos?.[0]?.activo !== true);
  const apoderados = todosApoderados.filter(a => a.activo);
  // Apoderados cesados (p. ej. los promotores, cuando el partido carga sus
  // apoderados definitivos). Se muestran en el histórico para que la nómina
  // fundacional no desaparezca de la vista al ser reemplazada.
  const apoderadosHistoricos = todosApoderados.filter(a => !a.activo);

  // ¿La nómina vigente es de autoridades PROMOTORAS (fundacionales)? Se detecta
  // por el mandato marcado esPromotor. Si es así, se rotula la nómina vigente
  // con el título "Autoridades promotoras (fundacionales)".
  const hayPromotorasVigentes = autoridadesVigentes.some(a => a.mandatos?.[0]?.esPromotor);

  // ¿Los apoderados vigentes son PROMOTORES (fundacionales)? Igual que las
  // autoridades: se rotula la sección "Apoderados promotores".
  const hayApoderadosPromotores = apoderados.some(a => a.esPromotor);

  const { data: partido } = useQuery({
    queryKey: ['partido', partidoId],
    queryFn: async () => {
      if (!partidoId) return null;
      const response = await partidoService.getById(partidoId);
      return response.data.data;
    },
    enabled: !!partidoId,
  });

  // Las reglas de PARIDAD, mínimo de autoridades y mínimo de apoderados son
  // requisitos del reconocimiento DEFINITIVO. No aplican mientras el partido está
  // en formación (reconocimiento con autoridades promotoras, antes del provisorio):
  // en esa etapa las autoridades son todas promotoras/fundacionales.
  const enFormacionReconocimiento =
    partido?.estado === 'EN_FORMACION' || partido?.estado === 'PENDIENTE_REVISION' || hayPromotorasVigentes;
  const mostrarReglasDefinitivo = !enFormacionReconocimiento;

  const { data: organosRaw = [] } = useQuery<Organo[]>({
    queryKey: ['organos', partidoId],
    queryFn: async () => {
      if (!partidoId) return [];
      const response = await partidoService.getOrganos(partidoId);
      return response.data.data as Organo[];
    },
    enabled: !!partidoId,
  });

  // Órganos obligatorios primero (mantiene el orden interno por `orden`)
  const organos = [...organosRaw].sort((a, b) => Number(b.esObligatorio) - Number(a.esObligatorio));

  const { data: partidos = [] } = useQuery({
    queryKey: ['partidos-select'],
    queryFn: async () => {
      const response = await partidoService.list({ pageSize: 200 });
      return response.data.data?.items ?? response.data.data ?? [];
    },
    enabled: esInterno && !paramId,
  });

  const puedeVerUsuarios = esAdminOSupervisor || ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');

  const { data: usuariosPartido = [], isLoading: usuariosLoading } = useQuery<UsuarioPartido[]>({
    queryKey: ['usuarios-partido', partidoId],
    queryFn: async () => {
      if (!partidoId) return [];
      const response = await usuarioService.list({ partidoId, pageSize: 200 });
      return (response.data.data?.items ?? response.data.data ?? []) as UsuarioPartido[];
    },
    enabled: !!partidoId && puedeVerUsuarios,
  });

  // En esta sección solo mostramos usuarios activos (con acceso al sistema)
  const usuariosActivos = usuariosPartido.filter(u => u.estado === 'ACTIVO');
  const dnisConUsuario = new Set(usuariosActivos.map(u => u.dni.replace(/\D/g, '')));

  // -------------------------------------------------------------------------
  // Paridad de género (sobre la nómina vigente)
  // -------------------------------------------------------------------------
  const calcularParidad = () => {
    if (autoridadesVigentes.length === 0) return null;
    const masculinos = autoridadesVigentes.filter(a => a.genero === 'MASCULINO').length;
    const femeninos = autoridadesVigentes.filter(a => a.genero === 'FEMENINO').length;
    const total = autoridadesVigentes.length;
    const diff = Math.abs(masculinos - femeninos);
    const cumple = diff <= Math.ceil(total * 0.3);
    return {
      masculinos,
      femeninos,
      otros: total - masculinos - femeninos,
      porcentajeMasc: Math.round((masculinos / total) * 100),
      porcentajeFem: Math.round((femeninos / total) * 100),
      cumple,
    };
  };
  const paridad = calcularParidad();

  const organoVencido = (o: Organo) =>
    !!o.fechaMandatoHasta && new Date(o.fechaMandatoHasta) < new Date();

  // =========================================================================
  // MODAL: Agregar autoridad a un órgano
  // =========================================================================
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'autoridad' | 'apoderado'>('autoridad');
  const [addOrgano, setAddOrgano] = useState<Organo | null>(null);
  const [addDni, setAddDni] = useState('');
  const [addBuscando, setAddBuscando] = useState(false);
  const [addEstado, setAddEstado] = useState<'idle' | 'no_padron' | 'duplicado' | 'ok'>('idle');
  const [addMensaje, setAddMensaje] = useState('');
  const [addPersona, setAddPersona] = useState<{ nombre: string; apellido: string; genero: string; anioNac?: string } | null>(null);
  const [addCargo, setAddCargo] = useState('');
  const [addCargoPers, setAddCargoPers] = useState('');
  const [addEsTitular, setAddEsTitular] = useState(true);
  const [addOrdenSuplencia, setAddOrdenSuplencia] = useState(0);
  const [addEmail, setAddEmail] = useState('');
  const [addTelefono, setAddTelefono] = useState('');
  const [addDocs, setAddDocs] = useState<{ existen: boolean; frente?: { path: string }; dorso?: { path: string } }>({ existen: false });
  const [addFileFrente, setAddFileFrente] = useState<File | null>(null);
  const [addFileDorso, setAddFileDorso] = useState<File | null>(null);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const resetAddState = () => {
    setAddDni('');
    setAddBuscando(false);
    setAddEstado('idle');
    setAddMensaje('');
    setAddPersona(null);
    setAddCargo('');
    setAddCargoPers('');
    setAddEsTitular(true);
    setAddOrdenSuplencia(0);
    setAddEmail('');
    setAddTelefono('');
    setAddDocs({ existen: false });
    setAddFileFrente(null);
    setAddFileDorso(null);
  };

  const openAddModal = (organo: Organo) => {
    setAddMode('autoridad');
    setAddOrgano(organo);
    resetAddState();
    setShowAddModal(true);
  };

  const openAddApoderado = () => {
    setAddMode('apoderado');
    setAddOrgano(null);
    resetAddState();
    setShowAddModal(true);
  };

  const buscarDniAdd = async () => {
    const dni = addDni.replace(/\D/g, '');
    if (dni.length < 7) {
      toast.error('Ingresá un DNI válido');
      return;
    }
    setAddBuscando(true);
    setAddEstado('idle');
    setAddPersona(null);
    try {
      // 1) Validar contra padrón
      const r = await padronService.validarDni(dni);
      const res = r.data.data;
      if (!res?.valido) {
        setAddEstado('no_padron');
        setAddMensaje(res?.mensaje || 'El DNI no figura en el padrón electoral provincial.');
        return;
      }
      // 2) Duplicado según el modo
      if (addMode === 'autoridad') {
        const yaEnOrgano = autoridadesVigentes.some(
          a => a.dni.replace(/\D/g, '') === dni && a.organo?.id === addOrgano?.id
        );
        if (yaEnOrgano) {
          setAddEstado('duplicado');
          setAddMensaje('Esta persona ya integra el mandato activo de este órgano. Una persona no puede ocupar dos cargos en el mismo mandato activo del mismo órgano.');
          return;
        }
      } else {
        const yaApoderado = apoderados.some(a => a.dni.replace(/\D/g, '') === dni);
        if (yaApoderado) {
          setAddEstado('duplicado');
          setAddMensaje('Esta persona ya es apoderado activo del partido.');
          return;
        }
      }
      const datos = res.datos || {};
      setAddPersona({
        nombre: datos.nombre || '',
        apellido: datos.apellido || '',
        genero: datos.genero || 'OTRO',
        anioNac: datos.anio_nacimiento ? String(datos.anio_nacimiento) : undefined,
      });
      // 3) ¿Ya tiene DNI cargado en el sistema?
      try {
        const c = await documentoService.checkDniPersona(dni);
        const d = c.data.data;
        if (d?.frente && d?.dorso) {
          setAddDocs({ existen: true, frente: { path: d.frente.path }, dorso: { path: d.dorso.path } });
        } else {
          setAddDocs({ existen: false });
        }
      } catch {
        setAddDocs({ existen: false });
      }
      setAddEstado('ok');
    } catch {
      setAddEstado('no_padron');
      setAddMensaje('No se pudo validar el DNI.');
    } finally {
      setAddBuscando(false);
    }
  };

  const submitAdd = async () => {
    if (addEstado !== 'ok' || !addPersona || !partidoId) return;
    const dni = addDni.replace(/\D/g, '');
    const cargoFinal = addCargo === 'OTRO' ? addCargoPers.trim() : addCargo;

    if (addMode === 'autoridad') {
      if (!addOrgano) return;
      if (!cargoFinal) { toast.error('Indicá el cargo'); return; }
      // Presidente no tiene suplente
      if (addCargo === 'PRESIDENTE' && !addEsTitular) { toast.error('El presidente no tiene suplente'); return; }
      // Presidente / Vicepresidente: un solo titular por órgano
      if (addEsTitular && (addCargo === 'PRESIDENTE' || addCargo === 'VICEPRESIDENTE')) {
        const yaTitular = autoridadesVigentes.some(a =>
          a.organo?.id === addOrgano.id &&
          a.mandatos?.[0]?.esTitular !== false &&
          a.mandatos?.[0]?.cargo === addCargo
        );
        if (yaTitular) { toast.error(`Ya hay un ${addCargo.toLowerCase()} titular en este órgano`); return; }
      }
    }
    if (!addDocs.existen && (!addFileFrente || !addFileDorso)) {
      toast.error('Subí el DNI (frente y dorso)');
      return;
    }

    const tipoFrente = addMode === 'autoridad' ? 'DNI_FRENTE_AUTORIDAD' : 'DNI_FRENTE_APODERADO';
    const tipoDorso = addMode === 'autoridad' ? 'DNI_DORSO_AUTORIDAD' : 'DNI_DORSO_APODERADO';

    setAddSubmitting(true);
    try {
      const titulo = `DNI ${addPersona.nombre} ${addPersona.apellido} (${dni})`;
      if (addDocs.existen) {
        await documentoService.vincularExistente({ tipo: tipoFrente, titulo: `${titulo} - Frente`, dniPersona: dni, partidoId, rutaArchivo: addDocs.frente!.path });
        await documentoService.vincularExistente({ tipo: tipoDorso, titulo: `${titulo} - Dorso`, dniPersona: dni, partidoId, rutaArchivo: addDocs.dorso!.path });
      } else {
        const fdFrente = new FormData();
        fdFrente.append('archivo', addFileFrente!);
        fdFrente.append('tipo', tipoFrente);
        fdFrente.append('titulo', `${titulo} - Frente`);
        fdFrente.append('partidoId', partidoId);
        fdFrente.append('dniPersona', dni);
        await documentoService.upload(fdFrente);

        const fdDorso = new FormData();
        fdDorso.append('archivo', addFileDorso!);
        fdDorso.append('tipo', tipoDorso);
        fdDorso.append('titulo', `${titulo} - Dorso`);
        fdDorso.append('partidoId', partidoId);
        fdDorso.append('dniPersona', dni);
        await documentoService.upload(fdDorso);
      }

      if (addMode === 'autoridad') {
        await partidoService.agregarAutoridadOrgano(partidoId, addOrgano!.id, {
          dni,
          nombre: addPersona.nombre,
          apellido: addPersona.apellido,
          genero: addPersona.genero,
          cargo: cargoFinal,
          esPresidente: cargoFinal === 'PRESIDENTE',
          esTitular: addEsTitular,
          ordenSuplencia: !addEsTitular && addOrdenSuplencia > 0 ? addOrdenSuplencia : undefined,
          email: addEmail || undefined,
          telefono: addTelefono || undefined,
        });
        toast.success('Autoridad agregada al órgano');
      } else {
        await partidoService.createApoderado(partidoId, {
          dni,
          nombre: addPersona.nombre,
          apellido: addPersona.apellido,
          genero: addPersona.genero,
          email: addEmail || undefined,
          telefono: addTelefono || undefined,
        });
        toast.success('Apoderado agregado');
      }

      queryClient.invalidateQueries({ queryKey: ['autoridades'] });
      queryClient.invalidateQueries({ queryKey: ['autoridades-dni'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-partido'] });
      setShowAddModal(false);
    } catch (err: any) {
      const details = err.response?.data?.error?.details as { message: string }[] | undefined;
      if (details && details.length > 0) details.forEach(d => toast.error(d.message, { duration: 7000 }));
      else toast.error(err.response?.data?.error?.message || 'Error al guardar');
    } finally {
      setAddSubmitting(false);
    }
  };

  // =========================================================================
  // MODAL: Editar autoridad (solo email + teléfono)
  // =========================================================================
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAut, setEditAut] = useState<Autoridad | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editTelefono, setEditTelefono] = useState('');
  const [editCargo, setEditCargo] = useState('');
  const [editCargoPers, setEditCargoPers] = useState('');

  const openEditModal = (aut: Autoridad) => {
    setEditAut(aut);
    setEditEmail(aut.email || '');
    setEditTelefono(aut.telefono || '');
    const cargoActual = aut.mandatos?.[0]?.cargo || '';
    if (cargoActual && !CARGOS_AUTORIDAD.includes(cargoActual)) {
      setEditCargo('OTRO');
      setEditCargoPers(cargoActual);
    } else {
      setEditCargo(cargoActual);
      setEditCargoPers('');
    }
    setShowEditModal(true);
  };

  const editarMutation = useMutation({
    mutationFn: (data: any) => partidoService.editarAutoridad(partidoId!, editAut!.id, data),
    onSuccess: () => {
      toast.success('Datos actualizados');
      queryClient.invalidateQueries({ queryKey: ['autoridades'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-partido'] });
      setShowEditModal(false);
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al actualizar'),
  });

  const submitEditarAutoridad = () => {
    const cargoFinal = editCargo === 'OTRO' ? editCargoPers.trim() : editCargo;
    if (!cargoFinal) { toast.error('Indicá el cargo'); return; }
    editarMutation.mutate({ email: editEmail || '', telefono: editTelefono || null, cargo: cargoFinal });
  };

  const editAutTieneUsuario = editAut ? dnisConUsuario.has(editAut.dni.replace(/\D/g, '')) : false;

  // =========================================================================
  // MODAL: Crear órgano (ADMIN / SUPERVISOR)
  // =========================================================================
  const [showOrganoModal, setShowOrganoModal] = useState(false);
  const [orgTipo, setOrgTipo] = useState('');
  const [orgDenomPers, setOrgDenomPers] = useState('');
  const [orgDesde, setOrgDesde] = useState('');
  const [orgHasta, setOrgHasta] = useState('');

  const openOrganoModal = () => {
    setOrgTipo('');
    setOrgDenomPers('');
    setOrgDesde('');
    setOrgHasta('');
    setShowOrganoModal(true);
  };

  // Solo bloquea si ya hay uno VIGENTE de ese tipo (un vencido no impide crear el reemplazo)
  const orgTipoExistente = orgTipo && orgTipo !== 'OTRO'
    ? organosRaw.find(o => o.tipoOrgano === orgTipo && !organoVencido(o))
    : undefined;

  const crearOrganoMutation = useMutation({
    mutationFn: (data: any) => partidoService.crearOrgano(partidoId!, data),
    onSuccess: () => {
      toast.success('Órgano creado');
      queryClient.invalidateQueries({ queryKey: ['organos'] });
      setShowOrganoModal(false);
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al crear el órgano'),
  });

  const bajarOrganoMutation = useMutation({
    mutationFn: (organoId: string) => partidoService.bajarOrgano(partidoId!, organoId),
    onSuccess: () => {
      toast.success('Órgano dado de baja');
      queryClient.invalidateQueries({ queryKey: ['organos'] });
      queryClient.invalidateQueries({ queryKey: ['autoridades'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al dar de baja el órgano'),
  });

  const handleBajarOrgano = (organo: Organo) => {
    const msg = `¿Dar de baja el órgano "${organoLabel(organo)}"?\n\n`
      + `Saldrá de la nómina vigente y quedará solo en el histórico (con su fecha real de vencimiento). Esta acción es para órganos que ya no existirán.`;
    if (!confirm(msg)) return;
    bajarOrganoMutation.mutate(organo.id);
  };

  // Obligatorios sin uno VIGENTE (falta, o el único de ese tipo está vencido) → habilitarlos al crear
  const obligatoriosFaltantes = TIPOS_ORGANO_OBLIGATORIO.filter(
    o => !organosRaw.some(x => x.tipoOrgano === o.value && !organoVencido(x))
  );
  const opcionesCrearOrgano = [...obligatoriosFaltantes, ...TIPOS_ORGANO_OPCIONAL];

  const submitCrearOrgano = (e: React.FormEvent) => {
    e.preventDefault();
    const tipoLabel = opcionesCrearOrgano.find(t => t.value === orgTipo)?.label || orgTipo;
    const denominacion = orgTipo === 'OTRO' ? orgDenomPers.trim() : tipoLabel;
    crearOrganoMutation.mutate({
      tipoOrgano: orgTipo,
      denominacion,
      denominacionPersonalizada: orgTipo === 'OTRO' ? orgDenomPers.trim() : undefined,
      fechaMandatoDesde: orgDesde,
      fechaMandatoHasta: orgHasta || null,
    });
  };

  // =========================================================================
  // MODAL: Editar apoderado (email + teléfono)
  // =========================================================================
  const [showEditApodModal, setShowEditApodModal] = useState(false);
  const [editApod, setEditApod] = useState<Apoderado | null>(null);
  const [editApodEmail, setEditApodEmail] = useState('');
  const [editApodTelefono, setEditApodTelefono] = useState('');

  const openEditApodModal = (ap: Apoderado) => {
    setEditApod(ap);
    setEditApodEmail(ap.email || '');
    setEditApodTelefono(ap.telefono || '');
    setShowEditApodModal(true);
  };

  const editApodTieneUsuario = editApod ? dnisConUsuario.has(editApod.dni.replace(/\D/g, '')) : false;

  const editApoderadoMutation = useMutation({
    mutationFn: (data: any) => partidoService.editarApoderado(partidoId!, editApod!.id, data),
    onSuccess: () => {
      toast.success('Datos de contacto actualizados');
      queryClient.invalidateQueries({ queryKey: ['autoridades'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-partido'] });
      setShowEditApodModal(false);
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al actualizar'),
  });

  // =========================================================================
  // Bajas
  // =========================================================================
  // Modal post-baja: la persona conserva usuario activo porque tiene otros cargos
  const [showUsuarioPendienteModal, setShowUsuarioPendienteModal] = useState(false);
  const [usuarioPendienteNombre, setUsuarioPendienteNombre] = useState('');
  const [usuarioPendienteDetalle, setUsuarioPendienteDetalle] = useState('');

  const bajarMutation = useMutation({
    mutationFn: (vars: { id: string; nombre: string; detalle: string }) => partidoService.bajarAutoridad(partidoId!, vars.id),
    onSuccess: (res: any, vars) => {
      const data = res?.data?.data;
      toast.success(data?.usuarioDadoDeBaja ? 'Autoridad y su usuario dados de baja' : 'Autoridad dada de baja');
      queryClient.invalidateQueries({ queryKey: ['autoridades'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-partido'] });
      if (data?.usuarioPendiente) { setUsuarioPendienteNombre(vars.nombre); setUsuarioPendienteDetalle(vars.detalle); setShowUsuarioPendienteModal(true); }
    },
    onError: (err: any) => {
      const details = err.response?.data?.error?.details as { message: string }[] | undefined;
      if (details && details.length > 0) details.forEach(d => toast.error(d.message, { duration: 6000 }));
      else toast.error(err.response?.data?.error?.message || 'Error al dar de baja');
    },
  });

  const bajarApoderadoMutation = useMutation({
    mutationFn: (vars: { id: string; nombre: string; detalle: string }) => partidoService.bajarApoderado(partidoId!, vars.id),
    onSuccess: (res: any, vars) => {
      const data = res?.data?.data;
      toast.success(data?.usuarioDadoDeBaja ? 'Apoderado y su usuario dados de baja' : 'Apoderado dado de baja');
      queryClient.invalidateQueries({ queryKey: ['autoridades'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-partido'] });
      if (data?.usuarioPendiente) { setUsuarioPendienteNombre(vars.nombre); setUsuarioPendienteDetalle(vars.detalle); setShowUsuarioPendienteModal(true); }
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al dar de baja'),
  });

  const handleRemove = (autoridad: Autoridad) => {
    const dni = autoridad.dni.replace(/\D/g, '');
    const otrosCargos = todasAutoridades.some(a => a.id !== autoridad.id && a.dni.replace(/\D/g, '') === dni && a.mandatos?.[0]?.activo === true);
    const esApoderado = apoderados.some(a => a.dni.replace(/\D/g, '') === dni);
    // Cartel de confirmación: solo la pregunta + qué pasa con el mandato
    const msg = `¿Dar de baja a ${autoridad.nombre} ${autoridad.apellido}?\n\n`
      + `Se cerrará su mandato con fecha de hoy y dejará de figurar en la nómina vigente (pasará al histórico junto con su órgano).`;
    if (!confirm(msg)) return;
    const partes: string[] = [];
    if (otrosCargos) partes.push('tiene otros cargos de autoridad vigentes');
    if (esApoderado) partes.push('es apoderado');
    bajarMutation.mutate({ id: autoridad.id, nombre: `${autoridad.nombre} ${autoridad.apellido}`, detalle: partes.join(' y ') });
  };

  const handleRemoveApoderado = (apoderado: Apoderado) => {
    const dni = apoderado.dni.replace(/\D/g, '');
    const esAutoridad = todasAutoridades.some(a => a.dni.replace(/\D/g, '') === dni && a.mandatos?.[0]?.activo === true);
    if (!confirm(`¿Dar de baja a ${apoderado.nombre} ${apoderado.apellido} como apoderado?`)) return;
    bajarApoderadoMutation.mutate({ id: apoderado.id, nombre: `${apoderado.nombre} ${apoderado.apellido}`, detalle: esAutoridad ? 'tiene cargos de autoridad vigentes' : '' });
  };

  // Advertencia de apoderados
  const cantApoderadosActivos = apoderados.length;
  const advertenciaApoderados = isLoading
    ? null
    : cantApoderadosActivos > 5
      ? `Se superó el límite de 5 apoderados (actuales: ${cantApoderadosActivos})`
      // "Se requiere al menos 1 apoderado" es requisito del reconocimiento
      // definitivo: no se muestra mientras el partido está en formación.
      : (mostrarReglasDefinitivo && cantApoderadosActivos < 1)
        ? 'Se requiere al menos 1 apoderado'
        : null;

  // -------------------------------------------------------------------------
  // Selector de partido (interno sin partido asignado)
  // -------------------------------------------------------------------------
  if (!partidoId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Autoridades del Partido</h1>
          <p className="mt-1 text-sm text-gray-500">Seleccioná un partido para ver sus autoridades</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6 max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">Partido</label>
          <select
            value={selectedPartidoId}
            onChange={e => setSelectedPartidoId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Seleccionar partido —</option>
            {(partidos as any[]).map((p: any) => (
              <option key={p.id} value={p.id}>{p.denominacion} ({p.sigla})</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render de una autoridad (vista vigente)
  // -------------------------------------------------------------------------
  const renderAutoridad = (aut: Autoridad) => (
    <div key={aut.id} className="px-5 py-3 hover:bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 text-gray-700">
            <span className="text-sm font-bold">{aut.nombre[0]}{aut.apellido[0]}</span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900">{aut.nombre} {aut.apellido}</p>
              {aut.mandatos?.[0]?.esPresidente && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Presidente</span>
              )}
            </div>
            <p className="text-xs font-semibold text-gray-600 mt-0.5">
              {aut.mandatos?.[0]?.cargo || (aut.mandatos?.[0]?.esPromotor ? 'Autoridad promotora' : '—')}
              {/* El origen promotor se muestra SIEMPRE, incluso cuando la promotora
                  tenía un cargo definido (ahí el texto de arriba muestra el cargo). */}
              {aut.mandatos?.[0]?.esPromotor && aut.mandatos?.[0]?.cargo && (
                <span className="text-teal-700 font-normal"> · promotora</span>
              )}
              {aut.mandatos?.[0]?.esTitular === false && (
                <span className="text-gray-400 font-normal">
                  {' '}· Suplente{aut.mandatos?.[0]?.ordenSuplencia ? ` (orden ${aut.mandatos[0].ordenSuplencia})` : ''}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">DNI {aut.dni}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-xs text-gray-400">
            {aut.email && <p>{aut.email}</p>}
            {aut.telefono && <p>{aut.telefono}</p>}
          </div>
          <div className="flex gap-3">
            {puedeGestionarAutoridades && (
              <button onClick={() => openEditModal(aut)} className="text-primary-600 hover:text-primary-800 text-xs">Editar datos</button>
            )}
            {esInterno && puedeGestionarAutoridades && aut.mandatos?.[0]?.activo === true && (
              <button
                onClick={() => handleRemove(aut)}
                disabled={bajarMutation.isPending}
                className="text-red-600 hover:text-red-800 text-xs disabled:opacity-50"
              >
                Bajar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Reglas de cargos por órgano (no reglamentado, salvo presidencia):
  //  - PRESIDENTE: un solo titular y SIN suplente.
  //  - VICEPRESIDENTE: un solo titular, puede tener suplente.
  //  - Los demás cargos: libres (varios titulares y suplentes).
  const autOrganoActivas = addOrgano ? autoridadesVigentes.filter(a => a.organo?.id === addOrgano.id) : [];
  const tieneTitularCargo = (cargo: string) =>
    autOrganoActivas.some(a => a.mandatos?.[0]?.esTitular !== false && a.mandatos?.[0]?.cargo === cargo);
  const hayPresidenteTitular = tieneTitularCargo('PRESIDENTE');
  const hayVicepresidenteTitular = tieneTitularCargo('VICEPRESIDENTE');
  const cargoSinSuplente = addCargo === 'PRESIDENTE'; // el presidente no tiene suplente
  const titularBloqueado =
    (addCargo === 'PRESIDENTE' && hayPresidenteTitular) ||
    (addCargo === 'VICEPRESIDENTE' && hayVicepresidenteTitular);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Autoridades y Órganos</h1>
          {esInterno && !paramId ? (
            <div className="mt-1 flex items-center gap-2">
              <select
                value={selectedPartidoId}
                onChange={e => setSelectedPartidoId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(partidos as any[]).map((p: any) => (
                  <option key={p.id} value={p.id}>{p.denominacion} ({p.sigla})</option>
                ))}
              </select>
            </div>
          ) : (
            <p className="mt-1 text-sm text-gray-500">{partido?.denominacion} ({partido?.sigla})</p>
          )}
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          <button
onClick={() => setVerHistorico(v => !v)}
            className={`px-4 py-2 rounded-lg border text-sm ${verHistorico ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            {verHistorico ? 'Volver a nóminas vigentes' : 'Ver histórico de nóminas'}
          </button>
{esAdminOSupervisor && !verHistorico && puedeAgregarMiembros && (
            <button
              onClick={openOrganoModal}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
            >
              ＋ Crear órgano
            </button>
          )}
        </div>
      </div>

      {/* Paridad de género (regla de reconocimiento definitivo; no en formación) */}
      {!verHistorico && mostrarReglasDefinitivo && paridad && (
        <div className={`rounded-lg p-4 ${paridad.cumple ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">{paridad.cumple ? '✅' : '⚠️'}</span>
              <div>
                <p className={`font-medium ${paridad.cumple ? 'text-green-800' : 'text-yellow-800'}`}>
                  {paridad.cumple ? 'Cumple con paridad de género' : 'No cumple con paridad de género'}
                </p>
                <p className="text-sm text-gray-600">
                  Masculino: {paridad.masculinos} ({paridad.porcentajeMasc}%) | Femenino: {paridad.femeninos} ({paridad.porcentajeFem}%)
                  {paridad.otros > 0 && ` | Otros: ${paridad.otros}`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{autoridadesVigentes.length}</p>
              <p className="text-sm text-gray-500">autoridades</p>
            </div>
          </div>
        </div>
      )}

      {/* Título grande cuando la nómina vigente es la fundacional (promotoras) */}
      {!verHistorico && hayPromotorasVigentes && (
        <div className="border-l-4 border-teal-500 pl-4">
          <h2 className="text-2xl font-bold text-teal-900">Autoridades promotoras (fundacionales)</h2>
          <p className="text-sm text-gray-600">Nómina declarada en el acta constitutiva. Se conservará como registro histórico cuando se carguen las autoridades definitivas.</p>
        </div>
      )}

      {/* ----------------------------- LISTA ----------------------------- */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      ) : verHistorico ? (
        /* ───────────── Histórico (nóminas reemplazadas) ───────────── */
        <div className="space-y-3">
          {autoridadesHistoricas.length === 0 && apoderadosHistoricos.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              No hay nóminas anteriores registradas
            </div>
          ) : (() => {
            // Agrupar por órgano y, dentro, por MANDATO DEL ÓRGANO (clave: fecha de inicio).
            // El fin del mandato = la fecha de cierre más tardía del grupo.
            const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-AR') : '—');
            const organosMap = new Map<string, { organoNombre: string; periodos: Map<string, { desde?: string; organo?: Autoridad['organo']; items: Autoridad[] }> }>();
            for (const aut of autoridadesHistoricas) {
              const oKey = aut.organo?.id ?? '__sin__';
              if (!organosMap.has(oKey)) organosMap.set(oKey, { organoNombre: organoLabel(aut.organo ?? { denominacion: 'Sin órgano' }), periodos: new Map() });
              const m = aut.mandatos?.[0];
              const pKey = m?.mandatoDesde ?? '';
              const periodos = organosMap.get(oKey)!.periodos;
              if (!periodos.has(pKey)) periodos.set(pKey, { desde: m?.mandatoDesde, organo: aut.organo, items: [] });
              periodos.get(pKey)!.items.push(aut);
            }
            return [...organosMap.entries()].map(([oKey, grupo]) => {
              const periodos = [...grupo.periodos.entries()].sort((a, b) => (b[1].desde ?? '').localeCompare(a[1].desde ?? ''));
              return (
                <div key={oKey} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-5 py-3 border-b bg-gray-100 border-l-4 border-gray-500">
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">{grupo.organoNombre}</span>
                    <span className="ml-2 text-xs text-gray-400">{periodos.length} {periodos.length === 1 ? 'mandato' : 'mandatos'}</span>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {periodos.map(([pKey, periodo]) => {
                      const fullKey = `${oKey}::${pKey}`;
                      const abierto = histExpandido[fullKey] === true;
                      // Fechas REALES del órgano para el encabezado del mandato.
                      // Si el órgano no las tiene, se infieren de las autoridades.
                      const finInferido = periodo.items.reduce<string | null>((max, a) => {
                        const h = a.mandatos?.[0]?.mandatoHasta ?? null;
                        return h && (!max || h > max) ? h : max;
                      }, null);
                      const mandatoDesde = periodo.organo?.fechaMandatoDesde ?? periodo.desde ?? null;
                      const mandatoHasta = periodo.organo?.fechaMandatoHasta ?? finInferido;
                      return (
                        <div key={fullKey} className={abierto ? 'border-l-4 border-primary-500' : 'border-l-4 border-transparent'}>
                          <button
                            onClick={() => setHistExpandido(p => ({ ...p, [fullKey]: !abierto }))}
                            className={`w-full px-5 py-3 flex items-center justify-between text-left ${abierto ? 'bg-primary-50' : 'bg-gray-50 hover:bg-gray-100'}`}
                          >
                            <span className="text-sm font-semibold text-gray-700">
                              {/* Si toda la nómina del período era promotora, se rotula
                                  como tal (es la nómina fundacional del partido). */}
                              {periodo.items.every(a => a.mandatos?.[0]?.esPromotor)
                                ? 'Nómina promotora (fundacional)'
                                : 'Mandato del órgano'}: {fmt(mandatoDesde)} → {fmt(mandatoHasta)}
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-500">{periodo.items.length} aut.</span>
                              <svg className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>
                          {abierto && (
                            <div className="divide-y">
                              {periodo.items.map(aut => {
                                const m = aut.mandatos?.[0];
                                // Mostrar la fecha propia de la autoridad solo si difiere de la del órgano
                                const desdeDistinto = !!m?.mandatoDesde && fmt(m.mandatoDesde) !== fmt(mandatoDesde);
                                const hastaDistinto = !!m?.mandatoHasta && fmt(m.mandatoHasta) !== fmt(mandatoHasta);
                                return (
                                  <div key={aut.id} className="px-5 py-3 pl-6">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-sm font-medium text-gray-700">{aut.nombre} {aut.apellido}</p>
                                      {m?.esPromotor && (
                                        <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Autoridad Promotora</span>
                                      )}
                                    </div>
                                    <p className="text-xs font-semibold text-gray-500">
                                      {m?.cargo || (m?.esPromotor ? 'Autoridad promotora' : '—')}
                                      {m?.esTitular === false && <span className="text-gray-400 font-normal"> · Suplente</span>}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      DNI {aut.dni}
                                      {desdeDistinto && <span className="ml-1 text-amber-600">· Asumió el {fmt(m?.mandatoDesde)}</span>}
                                      {hastaDistinto && <span className="ml-1 text-red-400">· Baja el {fmt(m?.mandatoHasta)}</span>}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}

          {/* Apoderados cesados. Conservan su título ("Apoderado Promotor") para
              que la nómina fundacional siga identificable en el histórico. */}
          {apoderadosHistoricos.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-100 border-l-4 border-gray-500">
                <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Apoderados anteriores</span>
                <span className="ml-2 text-xs text-gray-400">{apoderadosHistoricos.length}</span>
              </div>
              <div className="divide-y">
                {apoderadosHistoricos.map(ap => (
                  <div key={ap.id} className="px-5 py-3">
                    <p className="text-sm font-medium text-gray-700">{ap.nombre} {ap.apellido}</p>
                    <p className="text-xs font-semibold text-gray-500">{ap.esPromotor ? 'Apoderado Promotor' : 'Apoderado'}</p>
                    <p className="text-xs text-gray-400">
                      DNI {ap.dni}
                      {ap.fechaCese && <span className="ml-1 text-red-400">· Cesó el {new Date(ap.fechaCese).toLocaleDateString('es-AR')}</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ───────────── Vista vigente por órgano ───────────── */
        <div className="space-y-3">
          {organos.map(organo => {
            const vencido = organoVencido(organo);
            let autoridadesOrgano = autoridadesVigentes.filter(a => a.organo?.id === organo.id);
            // Solo si el órgano está VENCIDO y no le quedan autoridades activas,
            // mostramos su última nómina conocida. Si el órgano sigue vigente,
            // mostramos únicamente las autoridades activas (las dadas de baja no aparecen).
            if (vencido && autoridadesOrgano.length === 0) {
              const hist = autoridadesHistoricas.filter(a => a.organo?.id === organo.id);
              if (hist.length > 0) {
                const maxDesde = hist.reduce((m, a) => {
                  const d = a.mandatos?.[0]?.mandatoDesde ?? '';
                  return d > m ? d : m;
                }, '');
                autoridadesOrgano = hist.filter(a => (a.mandatos?.[0]?.mandatoDesde ?? '') === maxDesde);
              }
            }
            const isOpen = organoExpandido[organo.id] === true;
            const titulares = autoridadesOrgano.filter(a => a.mandatos?.[0]?.esTitular !== false);
            const suplentes = autoridadesOrgano.filter(a => a.mandatos?.[0]?.esTitular === false);

            return (
              <div key={organo.id} className={`bg-white rounded-lg shadow overflow-hidden ${isOpen ? (vencido ? 'border-2 border-red-600' : 'border-2 border-primary-600') : vencido ? 'ring-1 ring-red-100' : ''}`}>
                <div className={`w-full px-5 py-4 flex items-center justify-between gap-2 border-l-4 ${vencido ? 'border-red-400 bg-red-50/40' : organo.esObligatorio ? 'border-primary-600' : 'border-gray-300'}`}>
                  {vencido && esAdminOSupervisor && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleBajarOrgano(organo); }}
                      disabled={bajarOrganoMutation.isPending}
                      title="Quitar organo de nomina vigente (pasar al histórico)"
                      className="flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-red-600 hover:bg-red-100 disabled:opacity-50"
                    >
                      ✕
                    </button>
                  )}
                  <button onClick={() => setOrganoExpandido(p => ({ ...p, [organo.id]: !isOpen }))} className="flex-1 text-left">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-bold text-gray-900 uppercase tracking-wide">{organoLabel(organo)}</span>
                      {(organo.fechaMandatoDesde || organo.fechaMandatoHasta) && (
                        <span className={`text-xs ${vencido ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                          Mandato:{' '}
                          {organo.fechaMandatoDesde ? new Date(organo.fechaMandatoDesde).toLocaleDateString('es-AR') : '—'}
                          {organo.fechaMandatoHasta ? ` → ${new Date(organo.fechaMandatoHasta).toLocaleDateString('es-AR')}` : ''}
                          {vencido && ' · Vencido'}
                        </span>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    {puedeGestionarAutoridades && puedeAgregarMiembros && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openAddModal(organo); }}
                        className="px-2.5 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                      >
                        ＋ Agregar autoridad
                      </button>
                    )}
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${vencido ? 'bg-red-100 text-red-700' : organo.esObligatorio ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {vencido ? 'VENCIDO' : organo.esObligatorio ? 'OBLIGATORIO' : 'OPCIONAL'}
                    </span>
                    <button onClick={() => setOrganoExpandido(p => ({ ...p, [organo.id]: !isOpen }))}>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t divide-y">
                    {vencido && (
                      <div className="px-5 py-2.5 bg-red-50 text-xs text-red-700 flex items-center gap-2">
                        <span>⚠️</span> Última nómina conocida con mandato vencido. Las autoridades que agregues se crearán con el mismo mandato vencido del órgano.
                      </div>
                    )}
                    {autoridadesOrgano.length === 0 ? (
                      <p className="px-5 py-4 text-sm text-gray-400">Sin autoridades en este órgano.</p>
                    ) : (
                      <>
                        {titulares.length > 0 && (
                          <>
                            <div className="px-5 py-2 bg-gray-50"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Titulares</span></div>
                            {titulares.map(renderAutoridad)}
                          </>
                        )}
                        {suplentes.length > 0 && (
                          <>
                            <div className="px-5 py-2 bg-gray-50"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Suplentes</span></div>
                            {suplentes.map(renderAutoridad)}
                          </>
                        )}
                      </>
                    )}
                    {puedeGestionarAutoridades && puedeAgregarMiembros && (
                      <div className="px-5 py-3 bg-gray-50/60">
                        <button onClick={() => openAddModal(organo)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                          ＋ Agregar autoridad a este órgano
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Autoridades sin órgano asignado */}
          {(() => {
            const sinOrgano = autoridadesVigentes.filter(a => !a.organo?.id);
            if (sinOrgano.length === 0) return null;
            return (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-500">Sin órgano asignado</span>
                  <span className="text-xs text-gray-400">{sinOrgano.length} aut.</span>
                </div>
                <div className="divide-y">{sinOrgano.map(renderAutoridad)}</div>
              </div>
            );
          })()}

          {autoridadesVigentes.length === 0 && organos.length === 0 && (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">No hay autoridades registradas</div>
          )}
        </div>
      )}

      {/* ----------------------------- APODERADOS ----------------------------- */}
      {!verHistorico && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{hayApoderadosPromotores ? 'Apoderados Promotores' : 'Apoderados'} ({apoderados.length})</h2>
            {puedeGestionarAutoridades && puedeAgregarMiembros && (
              <button
                onClick={openAddApoderado}
                className="text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                ＋ Agregar apoderado{hayApoderadosPromotores ? ' promotor' : ''}
              </button>
            )}
          </div>
          {advertenciaApoderados && (
            <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-sm text-yellow-800 flex items-center gap-2">
              <span>⚠️</span> {advertenciaApoderados}
            </div>
          )}
          {isLoading ? (
            <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div></div>
          ) : apoderados.length > 0 ? (
            <div className="divide-y">
              {apoderados.map(ap => (
                <div key={ap.id} className="px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold">{ap.nombre[0]}{ap.apellido[0]}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-gray-900">{ap.nombre} {ap.apellido}</p>
                          {ap.esIniciador && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">Iniciador</span>}
                        </div>
                        <p className="text-xs font-semibold text-gray-600 mt-0.5">{ap.esPromotor ? 'Apoderado Promotor' : 'Apoderado'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">DNI {ap.dni}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right text-xs text-gray-400">
                        {ap.email && <p>{ap.email}</p>}
                        {ap.telefono && <p>{ap.telefono}</p>}
                      </div>
                      <div className="flex gap-3">
                        {puedeGestionarAutoridades && (
                          <button onClick={() => openEditApodModal(ap)} className="text-primary-600 hover:text-primary-800 text-xs">Editar datos</button>
                        )}
                        {puedeGestionarAutoridades && ap.activo && (
                          <button onClick={() => handleRemoveApoderado(ap)} disabled={bajarApoderadoMutation.isPending} className="text-red-600 hover:text-red-800 text-xs disabled:opacity-50">Bajar</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No hay apoderados registrados</div>
          )}
        </div>
      )}

      {/* ----------------------------- USUARIOS DEL PARTIDO ----------------------------- */}
      {!verHistorico && puedeVerUsuarios && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Usuarios Activos (con acceso al sistema) ({usuariosActivos.length})</h2>
            <button
              onClick={() => navigate('/usuarios')}
              className="text-xs text-primary-600 hover:text-primary-800 font-medium whitespace-nowrap"
            >
              Gestionar usuarios
            </button>
          </div>
          {usuariosLoading ? (
            <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div></div>
          ) : usuariosActivos.length > 0 ? (
            <div className="divide-y">
              {usuariosActivos.map(u => (
                <div key={u.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{u.nombre} {u.apellido}</p>
                    <p className="text-xs text-gray-400 mt-0.5">DNI {u.dni} · {u.rol}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right text-xs text-gray-400">
                      {u.email && <p>{u.email}</p>}
                      {u.telefono && <p>{u.telefono}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No hay usuarios activos para este partido</div>
          )}
        </div>
      )}

      {/* ======================= MODAL: AGREGAR AUTORIDAD / APODERADO ======================= */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={addMode === 'autoridad' ? 'Agregar autoridad' : (hayApoderadosPromotores ? 'Agregar apoderado promotor' : 'Agregar apoderado')} size="lg">
        <div className="space-y-4">
          {addMode === 'autoridad' && (
            <p className="text-xs text-gray-500">Órgano: <span className="font-semibold">{addOrgano ? organoLabel(addOrgano) : ''}</span></p>
          )}

          {addMode === 'autoridad' && addOrgano && organoVencido(addOrgano) && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
              ⚠️ Este órgano tiene el mandato vencido. La autoridad se creará con ese mismo mandato vencido.
            </div>
          )}

          {/* DNI + búsqueda */}
          <div>
            <label className="block text-sm font-medium mb-1">DNI *</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={addDni}
                onChange={e => { setAddDni(e.target.value.replace(/\D/g, '')); setAddEstado('idle'); setAddPersona(null); }}
                maxLength={8}
                placeholder="Sin puntos"
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              />
              <button onClick={buscarDniAdd} disabled={addBuscando} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
                {addBuscando ? 'Buscando…' : 'Buscar'}
              </button>
            </div>
          </div>

          {addEstado === 'no_padron' && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">⚠️ {addMensaje}</div>
          )}
          {addEstado === 'duplicado' && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">⛔ {addMensaje}</div>
          )}

          {addEstado === 'ok' && addPersona && (
            <div className="space-y-4">
              <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-700">
                ✔ Encontrado en el padrón: <b>{addPersona.nombre} {addPersona.apellido}</b>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre</label>
                  <input value={addPersona.nombre} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Apellido</label>
                  <input value={addPersona.apellido} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Género</label>
                  <input value={generoLabels[addPersona.genero] || addPersona.genero} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Año de nacimiento</label>
                  <input value={addPersona.anioNac || '—'} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
                </div>
              </div>

              {addMode === 'autoridad' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Cargo *</label>
                      <select
                        value={addCargo}
                        onChange={e => {
                          const c = e.target.value;
                          setAddCargo(c);
                          if (c === 'PRESIDENTE') setAddEsTitular(true); // sin suplente
                          else if (c === 'VICEPRESIDENTE' && hayVicepresidenteTitular) setAddEsTitular(false);
                        }}
                        className="w-full border rounded-lg px-3 py-2 text-sm uppercase"
                      >
                        <option value="">Seleccionar</option>
                        {CARGOS_AUTORIDAD.map(c => {
                          // PRESIDENTE no admite 2º titular ni suplente: si ya hay, se deshabilita
                          const presidenteOcupado = c === 'PRESIDENTE' && hayPresidenteTitular;
                          return <option key={c} value={c} disabled={presidenteOcupado}>{c}{presidenteOcupado ? ' (ya designado)' : ''}</option>;
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Condición</label>
                      <div className="flex gap-3 mt-2">
                        <label className={`flex items-center gap-1 text-sm ${titularBloqueado ? 'opacity-40 cursor-not-allowed' : ''}`}>
                          <input type="radio" checked={addEsTitular} disabled={titularBloqueado} onChange={() => setAddEsTitular(true)} /> Titular
                        </label>
                        <label className={`flex items-center gap-1 text-sm ${cargoSinSuplente ? 'opacity-40 cursor-not-allowed' : ''}`}>
                          <input type="radio" checked={!addEsTitular} disabled={cargoSinSuplente} onChange={() => setAddEsTitular(false)} /> Suplente
                        </label>
                      </div>
                      {cargoSinSuplente && (
                        <p className="mt-1 text-[11px] text-gray-500">El presidente no tiene suplente (lo cubre el vicepresidente).</p>
                      )}
                      {titularBloqueado && !cargoSinSuplente && (
                        <p className="mt-1 text-[11px] text-amber-600">Ya hay un titular para este cargo: solo podés agregarlo como suplente.</p>
                      )}
                    </div>
                  </div>

                  {addCargo === 'OTRO' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Especificar cargo *</label>
                      <input value={addCargoPers} onChange={e => setAddCargoPers(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                    </div>
                  )}
                  {!addEsTitular && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Orden de suplencia</label>
                      <input type="number" min={0} value={addOrdenSuplencia} onChange={e => setAddOrdenSuplencia(Number(e.target.value))} className="w-32 border rounded-lg px-3 py-2 text-sm" />
                    </div>
                  )}
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                  <input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)} placeholder="opcional" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Teléfono</label>
                  <input value={addTelefono} onChange={e => setAddTelefono(e.target.value)} placeholder="opcional" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Documentación DNI */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-gray-600 mb-2">Documentación · DNI (frente y dorso) *</p>
                {addDocs.existen ? (
                  <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-700">
                    ✔ Esta persona ya tiene su DNI cargado en el sistema. Se vinculará el archivo existente — no hace falta volver a subirlo.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400">
                      <span className="text-xs text-gray-500 block">{addFileFrente ? `✔ ${addFileFrente.name}` : 'DNI Frente'}</span>
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => setAddFileFrente(e.target.files?.[0] || null)} />
                    </label>
                    <label className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400">
                      <span className="text-xs text-gray-500 block">{addFileDorso ? `✔ ${addFileDorso.name}` : 'DNI Dorso'}</span>
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => setAddFileDorso(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button
              onClick={submitAdd}
              disabled={addEstado !== 'ok' || addSubmitting}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {addSubmitting ? 'Guardando…' : addMode === 'autoridad' ? 'Agregar al órgano' : 'Agregar apoderado'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ======================= MODAL: EDITAR AUTORIDAD ======================= */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Editar autoridad" size="md">
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-900">{editAut?.nombre} {editAut?.apellido}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">DNI</label>
              <input value={editAut?.dni || ''} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Género</label>
              <input value={editAut ? (generoLabels[editAut.genero] || editAut.genero) : ''} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Cargo</label>
            <select value={editCargo} onChange={e => setEditCargo(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm uppercase">
              <option value="">Seleccionar</option>
              {CARGOS_AUTORIDAD.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">Solo cambia el cargo; el mandato (fechas) no se modifica.</p>
          </div>
          {editCargo === 'OTRO' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Especificar cargo</label>
              <input value={editCargoPers} onChange={e => setEditCargoPers(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
            <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Teléfono</label>
            <input value={editTelefono} onChange={e => setEditTelefono(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          {editAutTieneUsuario && (
            <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-xs text-green-700">
              👤 Esta persona tiene un usuario con el mismo DNI. El email y teléfono se actualizarán también en su usuario.
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button onClick={() => setShowEditModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button
              onClick={submitEditarAutoridad}
              disabled={editarMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {editarMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ======================= MODAL: CREAR ÓRGANO ======================= */}
      <Modal isOpen={showOrganoModal} onClose={() => setShowOrganoModal(false)} title="Crear órgano" size="md">
        <form onSubmit={submitCrearOrgano} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de órgano *</label>
            <select value={orgTipo} onChange={e => setOrgTipo(e.target.value)} required className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Seleccionar…</option>
              {obligatoriosFaltantes.length > 0 && (
                <optgroup label="Obligatorios faltantes">
                  {obligatoriosFaltantes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </optgroup>
              )}
              <optgroup label="Opcionales">
                {TIPOS_ORGANO_OPCIONAL.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </optgroup>
            </select>
          </div>
          {orgTipoExistente && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              ⚠️ Este partido ya tiene un órgano <b>{organoLabel(orgTipoExistente)}</b> vigente. No se puede duplicar un órgano vigente.
            </div>
          )}
          {orgTipo === 'OTRO' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Denominación personalizada *</label>
              <input value={orgDenomPers} onChange={e => setOrgDenomPers(e.target.value)} required className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Ej: Tribunal de Ética Partidaria" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mandato desde *</label>
              <input type="date" value={orgDesde} onChange={e => setOrgDesde(e.target.value)} required className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mandato hasta</label>
              <input type="date" value={orgHasta} onChange={e => setOrgHasta(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700">
            Tras crear el órgano vas a poder agregarle autoridades con el botón ＋ Agregar autoridad dentro de la tarjeta.
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button type="button" onClick={() => setShowOrganoModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button type="submit" disabled={!!orgTipoExistente || crearOrganoMutation.isPending} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
              {crearOrganoMutation.isPending ? 'Creando…' : 'Crear órgano'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ======================= MODAL: USUARIO PENDIENTE ======================= */}
      <Modal isOpen={showUsuarioPendienteModal} onClose={() => setShowUsuarioPendienteModal(false)} title="Usuario aún activo" size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <p className="text-sm text-gray-700">
              <b>{usuarioPendienteNombre}</b> aún posee un usuario activo en el sistema{usuarioPendienteDetalle ? <> porque {usuarioPendienteDetalle}</> : ' porque mantiene otros cargos vigentes'}.
              Si querés desactivar su usuario, hacelo desde <b>Usuarios</b>.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button onClick={() => setShowUsuarioPendienteModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cerrar</button>
            <button onClick={() => { setShowUsuarioPendienteModal(false); navigate('/usuarios'); }} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">Ir a Usuarios</button>
          </div>
        </div>
      </Modal>

      {/* ======================= MODAL: EDITAR APODERADO ======================= */}
      <Modal isOpen={showEditApodModal} onClose={() => setShowEditApodModal(false)} title="Editar apoderado" size="md">
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-900">{editApod?.nombre} {editApod?.apellido}</p>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">DNI</label>
            <input value={editApod?.dni || ''} readOnly className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
            <input type="email" value={editApodEmail} onChange={e => setEditApodEmail(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Teléfono</label>
            <input value={editApodTelefono} onChange={e => setEditApodTelefono(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          {editApodTieneUsuario && (
            <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-xs text-green-700">
              👤 Esta persona tiene un usuario con el mismo DNI. El email y teléfono se actualizarán también en su usuario.
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button onClick={() => setShowEditApodModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button
              onClick={() => editApoderadoMutation.mutate({ email: editApodEmail || '', telefono: editApodTelefono || null })}
              disabled={editApoderadoMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {editApoderadoMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
